import { useState, useEffect } from 'react';
import { Droplets, Check, ChevronRight, Loader2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface TaskData {
  schedule_id: string;
  plant_id: string;
  plant_name: string;
  plant_image: string;
  care_type: string;
  frequency_days: number;
  next_care_date: string;
  isCompleted: boolean;
  completedAt?: string;
  careLogId?: string;
}

export function TodayTasks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskData[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchTasks();
    }
  }, [user]);

  const fetchTasks = async () => {
    try {
      const today = new Date();
      const todayStr = format(today, 'yyyy-MM-dd');
      const tomorrowStr = format(addDays(today, 1), 'yyyy-MM-dd');

      console.log('[TodayTasks] Fetching tasks for date:', todayStr);

      // Fetch all schedules with plant info
      const { data: schedules, error: schedulesError } = await supabase
        .from('plant_care_schedules')
        .select(`
          id,
          plant_id,
          care_type,
          frequency_days,
          next_care_date,
          plant_identifications (
            popular_name,
            image_url
          )
        `)
        .order('next_care_date', { ascending: true });

      if (schedulesError) {
        console.error('[TodayTasks] Error fetching schedules:', schedulesError);
        return;
      }

      console.log('[TodayTasks] All schedules found:', schedules?.length || 0);
      schedules?.forEach(s => {
        console.log(`[TodayTasks] Schedule: plant_id=${s.plant_id}, next_care_date=${s.next_care_date}, care_type=${s.care_type}, name=${(s as any).plant_identifications?.popular_name}`);
      });

      // Fetch care logs completed today using date strings (more robust for timezone)
      const { data: todayLogs, error: logsError } = await supabase
        .from('care_logs')
        .select('id, schedule_id, completed_at')
        .gte('completed_at', `${todayStr}T00:00:00`)
        .lt('completed_at', `${tomorrowStr}T00:00:00`);

      if (logsError) {
        console.error('[TodayTasks] Error fetching today logs:', logsError);
        return;
      }

      console.log('[TodayTasks] Care logs completed today:', todayLogs?.length || 0);

      // Create a map of schedule_id -> care_log for quick lookup
      const logsMap = new Map(
        (todayLogs || []).map(log => [log.schedule_id, log])
      );

      // Build task list: pending tasks + completed today
      const taskList: TaskData[] = [];

      for (const schedule of schedules || []) {
        const todayLog = logsMap.get(schedule.id);
        const isPending = schedule.next_care_date <= todayStr;
        const isCompletedToday = !!todayLog;

        console.log(`[TodayTasks] Evaluating: name=${(schedule as any).plant_identifications?.popular_name}, next_care_date=${schedule.next_care_date}, todayStr=${todayStr}, isPending=${isPending}, isCompletedToday=${isCompletedToday}`);

        // Include if pending OR completed today
        if (isPending || isCompletedToday) {
          console.log(`[TodayTasks] INCLUDED in task list: ${(schedule as any).plant_identifications?.popular_name}`);
          taskList.push({
            schedule_id: schedule.id,
            plant_id: schedule.plant_id,
            plant_name: (schedule as any).plant_identifications?.popular_name || 'Planta',
            plant_image: (schedule as any).plant_identifications?.image_url || '',
            care_type: schedule.care_type,
            frequency_days: schedule.frequency_days,
            next_care_date: schedule.next_care_date,
            isCompleted: isCompletedToday,
            completedAt: todayLog?.completed_at,
            careLogId: todayLog?.id,
          });
        } else {
          console.log(`[TodayTasks] NOT included: ${(schedule as any).plant_identifications?.popular_name} (next_care_date > today)`);
        }
      }

      // Sort: pending first, then completed
      taskList.sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) {
          return a.isCompleted ? 1 : -1;
        }
        return 0;
      });

      console.log('[TodayTasks] Final task list count:', taskList.length);
      setTasks(taskList);
    } catch (error) {
      console.error('[TodayTasks] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (task: TaskData) => {
    if (!user) return;

    setProcessingId(task.schedule_id);

    try {
      // Create care log
      const { error: logError } = await supabase
        .from('care_logs')
        .insert({
          schedule_id: task.schedule_id,
          plant_id: task.plant_id,
          user_id: user.id,
          care_type: task.care_type,
        });

      if (logError) {
        console.error('Error creating log:', logError);
        toast.error('Erro ao registrar cuidado');
        return;
      }

      // Update next care date
      const nextDate = addDays(new Date(), task.frequency_days);
      const { error: updateError } = await supabase
        .from('plant_care_schedules')
        .update({ next_care_date: format(nextDate, 'yyyy-MM-dd') })
        .eq('id', task.schedule_id);

      if (updateError) {
        console.error('Error updating schedule:', updateError);
        toast.error('Erro ao atualizar agenda');
        return;
      }

      // Reload tasks to show updated status
      await fetchTasks();
      toast.success(`${task.plant_name} regada!`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao completar tarefa');
    } finally {
      setProcessingId(null);
    }
  };

  const handleUndo = async (task: TaskData) => {
    if (!user || !task.careLogId) return;

    setProcessingId(task.schedule_id);

    try {
      // Delete the care log
      const { error: deleteError } = await supabase
        .from('care_logs')
        .delete()
        .eq('id', task.careLogId);

      if (deleteError) {
        console.error('Error deleting log:', deleteError);
        toast.error('Erro ao desfazer');
        return;
      }

      // Reset next care date to today
      const { error: updateError } = await supabase
        .from('plant_care_schedules')
        .update({ next_care_date: format(new Date(), 'yyyy-MM-dd') })
        .eq('id', task.schedule_id);

      if (updateError) {
        console.error('Error updating schedule:', updateError);
        toast.error('Erro ao atualizar agenda');
        return;
      }

      // Reload tasks
      await fetchTasks();
      toast.success('Ação desfeita');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao desfazer');
    } finally {
      setProcessingId(null);
    }
  };

  const pendingCount = tasks.filter(t => !t.isCompleted).length;

  if (!user) return null;
  
  if (loading) {
    return (
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Droplets className="h-5 w-5 text-water" />
            Tarefas de Hoje
          </h2>
        </div>
        <div className="animate-pulse space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-20 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Droplets className="h-5 w-5 text-water" />
            Tarefas de Hoje
          </h2>
        </div>
        <Card className="glass-card">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
              <Check className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              Nenhuma tarefa pendente para hoje! 🎉
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 py-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Droplets className="h-5 w-5 text-water" />
          Tarefas de Hoje
        </h2>
        {pendingCount > 0 ? (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
            {pendingCount} {pendingCount === 1 ? 'pendente' : 'pendentes'}
          </span>
        ) : (
          <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
            Tudo feito!
          </span>
        )}
      </div>

      <div className="space-y-3">
        {tasks.map((task) => (
          <Card
            key={task.schedule_id}
            className={cn(
              "overflow-hidden transition-all duration-300",
              task.isCompleted 
                ? "bg-muted/50 border-muted" 
                : "glass-card",
              processingId === task.schedule_id && "opacity-50 scale-95"
            )}
          >
            <CardContent className="p-0">
              <div className="flex items-center gap-3 p-3">
                {/* Plant image */}
                <div
                  className={cn(
                    "w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer",
                    task.isCompleted && "opacity-70"
                  )}
                  onClick={() => navigate(`/plant/${task.plant_id}`)}
                >
                  <img
                    src={task.plant_image}
                    alt={task.plant_name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Task info */}
                <div className="flex-1 min-w-0">
                  <h3 className={cn(
                    "font-medium text-sm truncate",
                    task.isCompleted && "text-muted-foreground"
                  )}>
                    {task.plant_name}
                  </h3>
                  {task.isCompleted ? (
                    <div className="flex items-center gap-1 text-xs text-primary">
                      <Check className="h-3 w-3" />
                      <span>
                        Regada às {format(new Date(task.completedAt!), 'HH:mm', { locale: ptBR })}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-water">
                      <Droplets className="h-3 w-3" />
                      <span>Precisa de água</span>
                    </div>
                  )}
                </div>

                {/* Action button */}
                {task.isCompleted ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUndo(task)}
                    disabled={processingId === task.schedule_id}
                    className="h-9 px-3 text-muted-foreground hover:text-foreground"
                  >
                    {processingId === task.schedule_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Undo2 className="h-4 w-4 mr-1" />
                        Desfazer
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleComplete(task)}
                    disabled={processingId === task.schedule_id}
                    className="h-9 px-3 gradient-nature text-primary-foreground"
                  >
                    {processingId === task.schedule_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Feito
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
